import React from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import SVGIcon from '../SVGIcon/SVGIcon';

import {
  StyledButton,
  StyledButtonIconSpan,
  StyledButtonTextSpan,
  StyledIconButton
} from './Button.style';

/**
 * A button makes it possible for users to fulfill actions in the user interface.
 */

const Button = ({
  disabled = false,
  color = 'default',
  variant = 'text',
  className = '',
  contentType = 'text',
  icon = false,
  iconPosition = 'none',
  size = 'small',
  dataAttributes = {},
  onClick = false,
  children = null,
  ...buttonAttributes
}) => {
  const onClickProperty = {};
  if (onClick) onClickProperty.onClick = onClick;

  const buttonAttributesWithoutOnClick = omit(buttonAttributes, ['onClick']);

  /* Test data attributes */
  const testDataAttributes = dataAttributes;
  testDataAttributes['data-test-disabled'] = disabled;
  testDataAttributes['data-test-color'] = color;
  testDataAttributes['data-test-content-type'] = contentType;

  /* For the button contentType `toggle` guarantee the button size is set to small */
  size = contentType === 'toggle' ? 'small' : size;

  const renderIcon = () => {
    // For the button of contentType 'icon', let the size of the icon be defined by the icon button style.
    let iconSize = contentType === 'icon' ? 'inherit' : size;
    /* For the button contentType `toggle` guarantee the icon size is set to medium */
    iconSize = contentType === 'toggle' ? 'large' : iconSize;

    return (
      <StyledButtonIconSpan
        data-test-container="icon"
        data-test-variant={variant}
        $position={contentType === 'icon' ? 'none' : iconPosition}
        $size={size}
        $contentType={contentType}
      >
        <SVGIcon icon={icon} size={iconSize} />
      </StyledButtonIconSpan>
    );
  };

  const renderTextButton = (withIcon) => (
    <StyledButton
      disabled={disabled}
      $color={color}
      $size={size}
      $contentType={contentType}
      $variant={variant}
      className={className}
      {...onClickProperty}
      {...testDataAttributes}
      {...buttonAttributesWithoutOnClick}
    >
      {withIcon && iconPosition === 'left' && renderIcon()}
      <StyledButtonTextSpan>{children}</StyledButtonTextSpan>
      {withIcon && iconPosition === 'right' && renderIcon()}
    </StyledButton>
  );

  const renderIconButton = () => (
    <StyledIconButton
      $color={color}
      disabled={disabled}
      $variant={variant}
      $size={size}
      className={className}
      {...onClickProperty}
      {...testDataAttributes}
      {...buttonAttributesWithoutOnClick}
    >
      {renderIcon()}
    </StyledIconButton>
  );

  if (contentType !== 'icon')
    return renderTextButton(contentType === 'textWithIcon' || contentType === 'toggle');
  return renderIconButton();
};

Button.propTypes = {
  contentType: PropTypes.oneOf(['text', 'icon', 'textWithIcon', 'toggle']),
  color: PropTypes.oneOf(['default', 'primary']).isRequired,
  variant: PropTypes.oneOf(['text', 'outlined', 'filled']).isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']).isRequired,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  iconPosition: PropTypes.oneOf(['left', 'right', 'none']),
  disabled: PropTypes.bool.isRequired,
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className: PropTypes.string,
  dataAttributes: PropTypes.shape({}),
  children: PropTypes.node,
  onClick: PropTypes.oneOfType([PropTypes.func, PropTypes.bool])
};

export default Button;
